import React, { useState, useEffect } from 'react';
import { Form, useSubmit, useNavigation, useActionData } from '@remix-run/react';
import { InputField  } from '../inputs';
import InlineURLInputField from '../inputs/inlineUrlInputField';
import Button from '../buttons/button';
import { TypographyDisplay, TypographyParagraph, TypographyLabel, TypographyCaps } from '../typography';
import { Tooltip } from '../modalsAndOverlays';
import Callout from '../callout/callout';
import Dropdown, { selectOption } from '../common/dropdown';
import { chainOptions } from '#app/utils/misc';
import {
  ZoraIcon,
  SolanaFMIcon,
	InfoIcon,
	FileIcon,
	LinkIcon,
	BaseIcon,
	PolygonIcon,
	EthereumIcon,
  OpenSeaIcon,
  BlurIcon,
  EtherscanIcon,
	OpenInNewIcon,
} from '../icons';
import ShapeLogo from '#app/components/common/logos/ShapeLogo';
import TypographyHeader from '../typography/TypographyHeading';
import { Tag } from '../badgesAndTags';
import { PublicKey } from '@solana/web3.js';

// Validation functions
function isValidURL(link: string) {
  try {
    new URL(link);
    return true;
  } catch (e) {
    return false;
  }
}

function isValidContractAddress(contractAddress: string) {
  if (contractAddress === '') {
    return false;
  }
  return true;
}

function isValidTokenId(tokenId: string) {
  if (tokenId === '' || isNaN(Number(tokenId))) {
    return false;
  }
  return true;
}

function isValidTokenAddress(tokenAddress: string) {
  if (tokenAddress === '') {
    return false;
  }
  try {
    const isValidAddress = PublicKey.isOnCurve(tokenAddress);
    if (isValidAddress) {
      return true;
    } else {
      return false;
    }
  } catch (e) {
    return false;
  }
}

export const FindNFT = () => {
  const actionDataErrors = useActionData<{
    contractAddress?: string;
    tokenAddress?: string;
    tokenId?: string;
    chain?: string;
    link?: string;
    cid?: string;
  }>();
  const submit = useSubmit();
  const navigation = useNavigation();

  // State for marketplace URL
  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState('');

  // State for token information
  const [contractAddress, setContractAddress] = useState('');
  const [tokenId, setTokenId] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [chain, setChain] = useState<selectOption>(chainOptions[0]);
  const [tokenErrors, setTokenErrors] = useState({ 
    contractAddress: '', 
    tokenId: '',
    tokenAddress: '' 
  });

  // Update errors when action data is received
  useEffect(() => {
    if (actionDataErrors) {
      // Update UI errors from action data
      const newTokenErrors = { ...tokenErrors };
      
      if (actionDataErrors.contractAddress) {
        newTokenErrors.contractAddress = actionDataErrors.contractAddress;
      }
      
      if (actionDataErrors.tokenId) {
        newTokenErrors.tokenId = actionDataErrors.tokenId;
      }
      
      if (actionDataErrors.tokenAddress) {
        newTokenErrors.tokenAddress = actionDataErrors.tokenAddress;
      }
      
      if (actionDataErrors.link) {
        setLinkError(actionDataErrors.link);
      }
      
      setTokenErrors(newTokenErrors);
    }
  }, [actionDataErrors]);

  const handleMarketplaceSearch = (e: {
    preventDefault: () => void;
    currentTarget: HTMLFormElement | FormData | URLSearchParams | HTMLButtonElement | HTMLInputElement | { [name: string]: string } | null;
  }) => {
    e.preventDefault();
    if (!link) {
      setLinkError('Please enter a valid marketplace URL');
      return;
    }
    
    // Validate URL format
    if (!isValidURL(link)) {
      setLinkError('Please enter a valid URL');
      return;
    }
    
    setLinkError('');
    submit(e.currentTarget);
  };

  const handleTokenSearch = (e: {
    preventDefault: () => void;
    currentTarget: HTMLFormElement | FormData | URLSearchParams | HTMLButtonElement | HTMLInputElement | { [name: string]: string } | null;
  }) => {
    e.preventDefault();
    const errors = { 
      contractAddress: '', 
      tokenId: '',
      tokenAddress: ''
    };
    
    if (chain.slug === 'solana') {
      // Validate Solana token address
      if (!isValidTokenAddress(tokenAddress)) {
        errors.tokenAddress = `Invalid Token Address - ${tokenAddress}`;
      }
    } else {
      // Validate contract address and token ID for other chains
      if (!isValidContractAddress(contractAddress)) {
        errors.contractAddress = `Invalid Contract Address - ${contractAddress}`;
      }
      
      if (!isValidTokenId(tokenId)) {
        errors.tokenId = `Invalid Token ID - ${tokenId}`;
      }
    }
    
    setTokenErrors(errors);
    
    // Only submit if there are no errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (!hasErrors) {
      const formData = new FormData(e.currentTarget as HTMLFormElement);
      submit(e.currentTarget);
    }
  };

  return (
    <div className="w-full max-w-[90%] lg:max-w-[1440px] py-20 lg:px-[72px] mx-auto flex flex-col items-center gap-20">
			{/* Header */}
			<div className="flex flex-col sm:flex-row justify-between w-full">
				<TypographyDisplay size="large" additionalClasses="mb-4 text-textPrimaryLight dark:text-textPrimaryDark">
					FIND YOUR NFT
				</TypographyDisplay>				
      
				<div className="flex flex-col gap-4">
					<TypographyParagraph
						size="small"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						We support NFTs minted on the Ethereum, Zora, Polygon, Base, Shape, and Solana blockchains.
					</TypographyParagraph>
					
					<div className="flex flex-row flex-wrap gap-1">
						<Tag
							showIcon={true}
							variant="fill"
							icon={<EthereumIcon aria-label="Ethereum" />}
						>
							Ethereum
						</Tag>
						<Tag
							showIcon={true}
							variant="fill"
							icon={<BaseIcon aria-label="Base" />}
						>
							Base
						</Tag>
						<Tag
							showIcon={true}
							variant="fill"
							icon={<SolanaFMIcon aria-label="Solana" />}
						>
							Solana
						</Tag>
						<Tag
							showIcon={true}
							variant="fill"
							icon={<ZoraIcon aria-label="Zora" />}
						>
							Zora
						</Tag>
						<Tag
							showIcon={true}
							variant="fill"
							icon={<PolygonIcon aria-label="Polygon" />}
						>
							Polygon
						</Tag>
						<Tag
							showIcon={false}
							variant="fill"
							icon={<ShapeLogo aria-label="Shape" />}
						>
							Shape
						</Tag>
					</div>
				</div>
			</div>

			{/* Main Search Section */}
      <div className="flex flex-col w-full border rounded-lg border-uiPrimaryLight dark:border-uiSecondaryDark shadow-sm shadow-uiPrimaryLight dark:shadow-uiSecondaryDark ">
				{/* Header */}
				<div className="flex flex-row justify-between w-full bg-uiTertiaryLight dark:bg-uiTertiaryDark py-4 px-8">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Find an NFT's Atomic Lore page with a marketplace link or token information.
					</TypographyParagraph>
				</div>

				{/* Content */}
        <div className="flex flex-col w-full sm:flex-row gap-4 p-8">
          {/* Marketplace URL section */}
          <div className="w-full md:w-1/2 border rounded-lg border-uiPrimaryLight dark:border-uiSecondaryDark p-8 shadow-sm shadow-uiPrimaryLight dark:shadow-uiSecondaryDark">
            <Form method="post" onSubmit={handleMarketplaceSearch} className="w-full h-full">
              <div className="flex flex-col h-full gap-2">
                <div className="flex items-center gap-2 pb-6">
                  <span className="text-textSecondaryLight dark:text-textSecondaryDark">
                    <LinkIcon aria-label="LinkIcon" />
                  </span>
                  <TypographyHeader
                    size="small"
                    isBold
                    additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
                  >
                    Marketplace URL
                  </TypographyHeader>
                  <Tooltip
                    tip="Top"
                    content={'Copy and paste the full URL from your browser when viewing your NFT on any supported marketplaces.'}
                    width="min-w-[300px]"                    
                  >
                    <span className="text-textSecondaryLight dark:text-textSecondaryDark">
                      <InfoIcon aria-label="Info" size="small" />
                    </span>
                  </Tooltip>
                </div>

								<div className="">
									<TypographyCaps
										size="small"
										additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight mb-1"
									>
										MARKETPLACE URL
									</TypographyCaps>
									
									<InlineURLInputField
										name="link"
										type="text"
										placeholder="https://opensea.io/assets/ethereum/0x..."
										value={link}
										onChange={e => setLink(e.target.value)}
										containerWidth="w-full"
										isInactive={false}
										displayName={false}
										isError={!!linkError}
										helper={linkError}
									/>
								</div>

                <div className="flex flex-row mt-4 gap-2 flex-wrap">
									<Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://opensea.io/item/CHAIN/ADDRESS/TOKEN_ID");
											setLinkError("");
										}}
									>
										OpenSea
									</Tag>
									<Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://blur.io/asset/CHAIN/ADDRESS/TOKEN_ID");
											setLinkError("");
										}}
									>
										Blur
									</Tag>
									<Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://solana.fm/address/ADDRESS");
											setLinkError("");
										}}
									>
										SolanaFM
									</Tag>
									<Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://etherscan.io/token/ADDRESS?a=TOKEN_ID");
											setLinkError("");
										}}
									>
										Etherscan
									</Tag>
									<Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://exchange.art/single/ADDRESS/TOKEN_ID");
											setLinkError("");
										}}
									>
										Exchange.art
									</Tag>									
                  <Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://zora.co/collections/ADDRESS/TOKEN_ID");
											setLinkError("");
										}}
									>
										Zora
									</Tag>
                  <Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://shape.host/collections/ADDRESS/TOKEN_ID");
											setLinkError("");
										}}
									>
										Shape
									</Tag>
                  <Tag
										showIcon={false}
										variant="fill"
										onClick={() => {
											setLink("https://highlight.xyz/collection/ADDRESS");
											setLinkError("");
										}}
									>
										Highlight
									</Tag>
                </div>

                <Button
                  additionalClasses="mt-6 sm:mt-auto"
                  variant="primary"
                  size="large"
                  disabled={navigation.state !== 'idle' || !link.length}
                >
                  Search
                </Button>
              </div>
            </Form>
          </div>

          {/* Token Information section */}
          <div className="w-full md:w-1/2 border rounded-lg border-uiPrimaryLight dark:border-uiSecondaryDark p-8 shadow-sm shadow-uiPrimaryLight dark:shadow-uiSecondaryDark">
            <Form method="post" onSubmit={handleTokenSearch} className="w-full h-full">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 pb-6">									
                  <span className="text-textSecondaryLight dark:text-textSecondaryDark">
                    <FileIcon aria-label="File" /> 
                  </span>
                  <TypographyHeader
                    size="small"
										isBold
                    additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
                  >
										Token Information
                  </TypographyHeader>
                  <Tooltip
                    tip="Top"
                    content={'Need help finding this information? \n See our marketplace guides below.'}
                    width="min-w-[300px]"
                  >
                    <span className="text-textSecondaryLight dark:text-textSecondaryDark">
                      <InfoIcon aria-label="Info" size="small" />
                    </span>
                  </Tooltip>
                </div>

                <div className="">
                  <TypographyCaps
                    size="small"
                    additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight mb-1"
                  >
                    CHAIN
                  </TypographyCaps>
                  <Dropdown
                    options={chainOptions}
                    title=""
                    placeholder="Select a chain"
                    selected={chain}
                    onSelectionChange={selected => setChain(selected)}
                    containerClasses="w-full"
                    additionalClasses="py-3 text-textPrimaryLight dark:text-textPrimaryDark"
                  />
                  <input
                    type="hidden"
                    name="chain"
                    id="chain"
                    value={chain ? chain.slug : ''}
                  />
                </div>

                {chain.slug === 'solana' ? (
                  <div className="mb-4">
                    <TypographyCaps
                      size="small"
                      additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight mb-1"
                    >
                      TOKEN ADDRESS
                    </TypographyCaps>
                    <InputField
                      name="tokenAddress"
                      type="text"
                      placeholder="Enter Solana token address"
                      value={tokenAddress}
                      onChange={e => setTokenAddress(e.target.value)}
                      containerWidth="w-full"
                      additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
                      isError={!!tokenErrors.tokenAddress}
                      helper={tokenErrors.tokenAddress}
                      displayName={false}
                    />
                  </div>
                ) : (
                  <>
                    <div className="">
                      <TypographyCaps
                        size="small"
                        additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight mb-1"
                      >
                        CONTRACT ADDRESS
                      </TypographyCaps>
                      <InputField
                        name="contractAddress"
                        type="text"
                        placeholder="0x4d8bc9C0DeB2..."
                        value={contractAddress}
                        onChange={e => setContractAddress(e.target.value)}
                        containerWidth="w-full"
                        additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
                        isError={!!tokenErrors.contractAddress}
                        helper={tokenErrors.contractAddress}
                        displayName={false}
                      />
                    </div>

                    <div className="">
                      <TypographyCaps
                        size="small"
                        additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight mb-1"
                      >
                        TOKEN ID
                      </TypographyCaps>
                      <InputField
                        name="tokenId"
                        type="text"
                        placeholder="123"
                        value={tokenId}
                        onChange={e => setTokenId(e.target.value)}
                        containerWidth="w-full"
                        additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
                        isError={!!tokenErrors.tokenId}
                        helper={tokenErrors.tokenId}
                        displayName={false}
                      />
                    </div>
                  </>
                )}

                <Button
                  additionalClasses="mt-4"
                  variant="primary"
                  size="large"
                  disabled={
                    navigation.state !== 'idle' ||
                    !chain ||
                    (chain.slug === 'solana' && !tokenAddress) ||
                    (chain.slug !== 'solana' && (!tokenId || !contractAddress))
                  }
                >
                  Search
                </Button>
              </div>
            </Form>
          </div>
        </div>
        
				{/* Footer */}
        <div className="flex flex-row justify-between w-full bg-uiTertiaryLight dark:bg-uiTertiaryDark py-4 px-8">
          <TypographyParagraph
            size="small"
            additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark flex items-center"
          >
              <span className="text-textSecondaryLight dark:text-textSecondaryDark mr-1">
                <InfoIcon aria-label="Info" size="small" />
              </span>
              NFT data can be hard to find. Learn how to find your NFT's Token ID and Contract Address below.
          </TypographyParagraph>
        </div>
      </div>

			{/* How to find your NFT */}
			<div className="flex flex-col md:flex-row w-full gap-8">
				{/* Title Section */}
				<div className="w-full md:w-1/3 flex flex-col gap-3">
					<TypographyHeader size="large" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
						How to Find Your NFT's Token Information
					</TypographyHeader>
					{/* <div className="flex items-center gap-2">
						<Button variant="tertiary" size="small" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} onClick={() => window.open('/guides', '_blank')}>
							View guides for all marketplaces
						</Button>
					</div> */}
				</div>

				{/* Guides Section */}
				<div className="w-full md:w-2/3 flex flex-col gap-6">
					{/* Top row cards */}
					<div className="flex flex-col md:flex-row gap-4">
						{/* Opensea Card */}
						<div className="w-full md:w-1/2 p-6 bg-uiTertiaryLight dark:bg-uiTertiaryDark rounded-lg">
							<div className="flex flex-col gap-2 mb-4">
								<OpenSeaIcon aria-label="Opensea" className="text-textPrimaryLight dark:text-textPrimaryDark" />
								<TypographyHeader size="small" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
									On Opensea
								</TypographyHeader>
							</div>
							<ol className="space-y-1 text-textSecondaryLight dark:text-textSecondaryDark">
								<li><TypographyParagraph size="small">1. Open your NFT's page on OpenSea</TypographyParagraph></li>
								<li><TypographyParagraph size="small">2. Click on <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">About</span> tab menu to view the section</TypographyParagraph></li>
								<li><TypographyParagraph size="small">3. Click on <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">Blockchain Details</span> to expand</TypographyParagraph></li>
								<li><TypographyParagraph size="small">4. Copy "Contract Address" and "Token ID"</TypographyParagraph></li>
							</ol>
							<div className="flex justify-between mt-4">
								<div className="flex flex-wrap gap-1">
									<TypographyLabel size="small" isBold additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">Supported:</TypographyLabel>
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">ETH, Polygon, Base</TypographyLabel>
								</div>
								<Button variant="tertiary" size="small" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} onClick={() => window.open('https://opensea.io', '_blank')}>
									Go to Opensea
								</Button>
							</div>
						</div>

						{/* Blur Card */}
						<div className="w-full md:w-1/2 p-6 bg-uiTertiaryLight dark:bg-uiTertiaryDark rounded-lg">
							<div className="flex flex-col gap-2 mb-4">
								<BlurIcon aria-label="Blur" className="text-textPrimaryLight dark:text-textPrimaryDark" />
								<TypographyHeader size="small" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
									On Blur
								</TypographyHeader>
							</div>
							<ol className="space-y-1 text-textSecondaryLight dark:text-textSecondaryDark">
								<li><TypographyParagraph size="small">1. Open your NFT's page on Blur</TypographyParagraph></li>
								<li><TypographyParagraph size="small">2. In URL: <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">blur.io/asset/[CONTRACT]/[TOKEN_ID]</span></TypographyParagraph></li>
								<li><TypographyParagraph size="small">3. Contract Address is after <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">blur.io/asset/</span></TypographyParagraph></li>
								<li><TypographyParagraph size="small">4. Token ID is the final number</TypographyParagraph></li>
							</ol>
							<div className="flex justify-between mt-4">
								<div className="flex flex-wrap gap-1">
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">Supported:</TypographyLabel>
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">ETH, Polygon, Base</TypographyLabel>
								</div>
								<Button variant="tertiary" size="small" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} onClick={() => window.open('https://blur.io', '_blank')}>
									Go to Blur
								</Button>
							</div>
						</div>
					</div>

					{/* Pro tip */}
					<Callout 
						variant="success"
						content='Pro tip: Just copy the URL and use the "Marketplace URL" option'
					/>

					{/* Bottom row cards */}
					<div className="flex flex-col md:flex-row gap-4">
						{/* Etherscan Card */}
						<div className="w-full md:w-1/2 p-6 bg-uiTertiaryLight dark:bg-uiTertiaryDark rounded-lg">
							<div className="flex flex-col gap-2 mb-4">
								<EtherscanIcon aria-label="Etherscan" className="text-textPrimaryLight dark:text-textPrimaryDark" />
								<TypographyHeader size="small" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
									On Etherscan
								</TypographyHeader>
							</div>
							<ol className="space-y-1 text-textSecondaryLight dark:text-textSecondaryDark">
								<li><TypographyParagraph size="small">1. Search your NFT contract on Etherscan</TypographyParagraph></li>
								<li><TypographyParagraph size="small">2. Go to the <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">Token</span> tab</TypographyParagraph></li>
								<li><TypographyParagraph size="small">3. The NFT contract address is in the URL after: <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">token/</span></TypographyParagraph></li>
								<li><TypographyParagraph size="small">4. Find your token ID in the Token ID column</TypographyParagraph></li>
							</ol>
							<div className="flex justify-between mt-4">
								<div className="flex flex-wrap gap-1">
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">Supported:</TypographyLabel>
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">ETH, Polygon, Base</TypographyLabel>
								</div>
								<Button variant="tertiary" size="small" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} onClick={() => window.open('https://etherscan.io', '_blank')}>
									Go to Etherscan
								</Button>
							</div>
						</div>

						{/* Zora Card */}
						<div className="w-full md:w-1/2 p-6 bg-uiTertiaryLight dark:bg-uiTertiaryDark rounded-lg">
							<div className="flex flex-col gap-2 mb-4">
								<ZoraIcon aria-label="Zora" className="text-textPrimaryLight dark:text-textPrimaryDark" />
								<TypographyHeader size="small" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
									On Zora
								</TypographyHeader>
							</div>
							<ol className="space-y-1 text-textSecondaryLight dark:text-textSecondaryDark">
								<li><TypographyParagraph size="small">1. Navigate to your NFT's page on Zora</TypographyParagraph></li>
								<li><TypographyParagraph size="small">2. In URL: <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">zora.co/collections/[CONTRACT]/[TOKEN_ID]</span></TypographyParagraph></li>
								<li><TypographyParagraph size="small">3. The Contract Address is after <span className="font-mono text-textPrimaryLight dark:text-textPrimaryDark font-bold border border-uiPrimaryLight dark:border-uiSecondaryDark rounded-md px-1">collections/</span></TypographyParagraph></li>
								<li><TypographyParagraph size="small">4. The Token ID is the number at the end of the URL</TypographyParagraph></li>
							</ol>
							<div className="flex justify-between mt-4">
								<div className="flex flex-wrap gap-1">
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">Supported:</TypographyLabel>
									<TypographyLabel size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">ETH, Base, Zora Network</TypographyLabel>
								</div>
								<Button variant="tertiary" size="small" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} 	onClick={() => window.open('https://zora.co', '_blank')}>
									Go to Zora
								</Button>
							</div>
						</div>
					</div>

					{/* Pro tip for Zora */}
					<Callout 
						variant="success"
						content="Pro tip: Zora works on Ethereum, Base, and Zora chains - check which one your NFT uses."
					/>

					{/* View All Tutorials Button */}
					{/* <div className="flex justify-center mt-2">
						<Button variant="tertiary" size="medium" rightIcon={<OpenInNewIcon aria-label="OpenInNewIcon" />} onClick={() => window.open('/guides', '_blank')}>
							View All Tutorials
						</Button>
					</div> */}
				</div>
			</div>
    </div>
  );
};
